/**
 * Authentication Utilities for API Requests
 *
 * Provides utilities for detecting private endpoints and handling
 * authentication requirements in API calls.
 */

import type { BackendService } from './client';

/**
 * Error thrown when a private API endpoint is called without authentication.
 *
 * This error is intentionally silent and should not trigger user-facing notifications.
 * It's used to prevent unnecessary API calls when the user is not logged in.
 */
export class AuthRequiredError extends Error {
  constructor(
    public endpoint: string,
    public service?: string,
  ) {
    super(`Authentication required for ${endpoint}`);
    this.name = 'AuthRequiredError';
  }
}

/**
 * Service authentication strategy:
 *
 * - bff/oex: Default PUBLIC, only /private/ path requires auth
 * - nts/be: Default PRIVATE, only explicitly listed public endpoints don't require auth
 * - worker: Always PUBLIC (no auth required)
 */

/**
 * Known public endpoint patterns for different services.
 *
 * These patterns are used to identify endpoints that don't require authentication.
 * For nts/be services, this is the ONLY way to mark an endpoint as public.
 */
const PUBLIC_PATTERNS: Record<BackendService, RegExp[]> = {
  // BFF and OEX: most endpoints are public, only /private/ requires auth
  bff: [/\/public\//],
  oex: [/\/public\//],
  // NTS: default is PRIVATE, only these specific endpoints are public
  nts: [/\/public\//],
  // BE: default is PRIVATE, only these specific endpoints are public
  be: [/\/public\//],
  // Worker service - all public
  worker: [/.*/],
};

/**
 * Known private endpoint patterns that always require authentication.
 *
 * For bff/oex: these patterns override public defaults
 * For nts/be: not needed since default is already private
 */
const PRIVATE_PATTERNS: Record<BackendService, RegExp[]> = {
  bff: [/\/private\//],
  oex: [/\/private\//],
  // nts/be: empty because default is already PRIVATE
  nts: [],
  be: [],
  worker: [],
};

/**
 * Check if an endpoint is a private (authentication required) endpoint.
 *
 * Logic:
 * 1. First check if URL matches any known private patterns → return true
 * 2. Then check if URL matches any known public patterns → return false
 * 3. For bff/oex: default to public (most are public)
 * 4. For nts/be: default to private (most require auth)
 *
 * @param url - The API endpoint URL (relative to base URL)
 * @param service - The backend service name
 * @returns true if the endpoint requires authentication
 *
 * @example
 * isPrivateEndpoint('/v1/private/user-setting', 'bff') // true
 * isPrivateEndpoint('/v1/public/tickers', 'bff') // false
 * isPrivateEndpoint('/v1/get-account-info', 'nts') // true
 */
export function isPrivateEndpoint(
  url: string,
  service?: BackendService | string,
): boolean {
  const svc = (service as BackendService) || 'bff';

  // Get patterns for the service
  const privatePatterns = PRIVATE_PATTERNS[svc] || [];
  const publicPatterns = PUBLIC_PATTERNS[svc] || [];

  // Check private patterns first (takes precedence)
  for (const pattern of privatePatterns) {
    if (pattern.test(url)) {
      return true;
    }
  }

  // Check public patterns
  for (const pattern of publicPatterns) {
    if (pattern.test(url)) {
      return false;
    }
  }

  // Default behavior based on service
  // bff/oex: default to public (explicit /private/ required)
  // nts/be: default to private (most endpoints require auth)
  if (svc === 'bff' || svc === 'oex' || svc === 'worker') {
    return false;
  }

  // nts/be: default to private
  return true;
}

/**
 * Check if an error is an AuthRequiredError
 */
export function isAuthRequiredError(
  error: unknown,
): error is AuthRequiredError {
  return error instanceof AuthRequiredError;
}
